<div class="lab-monitoring-dialog">
  <!-- Header -->
  <div class="dialog-header">
    <div class="header-content">
      <div class="title-section">
        <h2 mat-dialog-title class="m-0 p-0">
          <mat-icon class="title-icon">monitor_heart</mat-icon>
          Continuous Lab Monitoring
        </h2>
      </div>
      <button mat-icon-button (click)="closeDialog()" class="close-button">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Controls -->
  <div class="dialog-controls">
    <div class="filter-controls">
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Search Tests</mat-label>
        <input matInput [(ngModel)]="searchQuery" (input)="onSearchChange()" placeholder="Search by test name or code...">
        <button mat-icon-button matSuffix *ngIf="searchQuery" (click)="clearSearch()" type="button" class="clear-button">
          <mat-icon>clear</mat-icon>
        </button>
        <mat-icon matSuffix *ngIf="!searchQuery" class="search-icon">search</mat-icon>
      </mat-form-field>

      <!-- Date Range Filter with Chips -->
      <div class="filter-group p-t-10">
        <mat-chip-listbox [(ngModel)]="selectedDateRange" (change)="onDateRangeChange($event)" [disabled]="isLoading">
          <mat-chip-option *ngFor="let chip of dateRangeChips" [value]="chip.value">
            {{ chip.label }}
          </mat-chip-option>
        </mat-chip-listbox>
      </div>

      <!-- Custom Date Range Controls -->
      <div class="custom-date-controls" *ngIf="selectedDateRange === 'custom'">
        <div class="date-inputs">
          <mat-form-field appearance="outline" class="date-field">
            <mat-label>Start Date</mat-label>
            <input matInput [matDatepicker]="startPicker" [(ngModel)]="customStartDate" 
                   (dateChange)="onCustomDateChange()" placeholder="DD/MM/YYYY" [max]="maxDate" [disabled]="isLoading">
            <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
            <mat-datepicker #startPicker></mat-datepicker>
          </mat-form-field>
          
          <mat-form-field appearance="outline" class="date-field">
            <mat-label>End Date</mat-label>
            <input matInput [matDatepicker]="endPicker" [(ngModel)]="customEndDate" 
                   (dateChange)="onCustomDateChange()" placeholder="DD/MM/YYYY" [max]="maxDate" [min]="customStartDate" [disabled]="isLoading">
            <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
            <mat-datepicker #endPicker></mat-datepicker>
          </mat-form-field>
        </div>
      </div>

      <!-- View Toggle -->
      <div class="view-toggle-group p-t-10">
        <mat-chip-listbox [(ngModel)]="viewMode" (change)="onViewModeChange()" [disabled]="isLoading">
          <mat-chip-option value="grid">
            Grid
          </mat-chip-option>
          <mat-chip-option value="table">
            Table
          </mat-chip-option>
          <mat-chip-option value="compare">
            Compare
          </mat-chip-option>
        </mat-chip-listbox>
      </div>
    </div>
  </div>

  <!-- Scrollable Content Area -->
  <div class="dialog-content">

    <!-- Loading State -->
    <div class="loading-container" *ngIf="isLoading">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Loading monitoring data...</p>
    </div>

    <!-- Monitoring Data -->
    <div class="monitoring-content" *ngIf="!isLoading">
    <div class="section-header">
      <h3>
        <mat-icon class="section-icon">science</mat-icon>
        Lab Values Overview ({{ filteredMonitoringData.length }} tests)
      </h3>
    </div>

    <!-- No Data State -->
    <div class="no-data" *ngIf="filteredMonitoringData.length === 0">
      <mat-icon class="no-data-icon">science</mat-icon>
      <p>No lab results found for the selected criteria.</p>
    </div>

    <!-- Grid View -->
    <div class="monitoring-grid" *ngIf="filteredMonitoringData.length > 0 && viewMode === 'grid'">
      <div *ngFor="let item of filteredMonitoringData" 
           class="monitoring-card" 
           [class.selected]="selectedTest === item.testCode"
           (click)="selectTest(item)"
           [matTooltip]="'Click to view detailed trend analysis'">
        <div class="card-header">
        <div class="test-info">
          <h4 class="test-name">
            <span class="test-code">{{ item.testCode }}</span>
            {{ item.testName }}
          </h4>
        </div>
          <div class="status-indicator" [ngClass]="item.status" *ngIf="!item.isMicrobiology">
            <mat-icon>{{ item.status === 'normal' ? 'check_circle' : 
                         item.status === 'critical' ? 'error' : 
                         item.status === 'abnormal' ? 'warning' :
                         item.status === 'high' || item.status === 'low' ? 'warning' : 'help_outline' }}</mat-icon>
          </div>
        </div>

        <div class="card-content">
          <div class="value-section">
            <div class="current-value">
              <span class="value">{{ item.latestValue }}</span>
              <span class="unit">{{ item.unit }}</span>
            </div>
            <div class="reference-range" *ngIf="item.latestValue && item.latestValue !== 'N/A' && item.latestValue !== '(No value)' && item.referenceRange && item.referenceRange !== 'N/A'">
              <span class="range-label">Reference:</span>
              <span class="range-value">{{ item.referenceRange }}</span>
            </div>
          </div>

          <div class="trend-section" *ngIf="item.trend !== 'no_data' && !item.isMicrobiology">
            <div class="trend-indicator" [ngClass]="item.trend">
              <mat-icon [ngClass]="getTrendColor(item.trend)">
                {{ getTrendIcon(item.trend) }}
              </mat-icon>
              <span class="trend-text">
                {{ item.trend === 'increasing' ? 'Rising' : 
                   item.trend === 'decreasing' ? 'Falling' : 'Stable' }}
                <span *ngIf="item.trendPercentage > 0">({{ item.trendPercentage | number:'1.1-1' }}%)</span>
              </span>
            </div>
          </div>
          <div class="microbiology-badge" *ngIf="item.isMicrobiology">
            <mat-icon>bacteria</mat-icon>
            <span>Microbiology</span>
          </div>

          <!-- Comment Section -->
          <div class="test-comment" *ngIf="item.latestComment">
            <div class="comment-header">
              <mat-icon>comment</mat-icon>
              <span>Comment</span>
            </div>
            <div class="comment-text">{{ item.latestComment }}</div>
          </div>

          <div class="date-section">
            <span class="latest-date">Last updated: {{ formatDate(item.latestDate) }}</span>
          </div>
        </div>

        <!-- History Section -->
        <div class="card-history" *ngIf="item.history.length > 1">
          <div class="history-header">
            <span>Recent History</span>
          </div>
          <div class="history-list">
            <div *ngFor="let historyItem of item.history.slice(0, 5)" 
                 class="history-item" 
                 [ngClass]="historyItem.status">
              <span class="history-value">{{ historyItem.value }} {{ item.unit }}</span>
              <span class="history-date">{{ formatDate(historyItem.date) }}</span>
            </div>
          </div>
        </div>

        <!-- Alerts for this test -->
        <div class="card-alerts" *ngIf="item.alerts.length > 0">
          <div *ngFor="let alert of item.alerts" class="test-alert" [ngClass]="alert.type">
            <mat-icon>{{ alert.type === 'critical' ? 'error' : 'warning' }}</mat-icon>
            <span>{{ alert.message }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Table View -->
    <div class="monitoring-table" *ngIf="filteredMonitoringData.length > 0 && viewMode === 'table'">
      <div class="table-container">
        <table class="lab-table">
          <thead>
            <tr>
              <th class="parameter-col">Parameter</th>
              <th class="value-col" *ngFor="let date of collectionDates; let i = index">
                <div class="date-header">
                  <span class="date">{{ formatTableDate(date) }}</span>
                  <span class="time">{{ formatTableTime(date) }}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let rowData of precomputedTableData" (click)="onTableRowClick(rowData.testCode)">
              <td class="parameter-cell">
                <div class="parameter-info">
                  <div class="parameter-name">{{ rowData.testName }}</div>
                  <div class="parameter-range">{{ rowData.referenceRange }}</div>
                </div>
              </td>
              <td class="value-cell" *ngFor="let dateKey of precomputedDateKeys; let i = index">
                <div class="value-display" 
                     [ngClass]="rowData.valuesByDate[dateKey]?.isCommentOnly ? 'comment-only' : (rowData.valuesByDate[dateKey]?.status || 'empty')">
                  <!-- Comment-only entries (like NGPOS procedures) -->
                  <div class="comment-content" *ngIf="rowData.valuesByDate[dateKey]?.isCommentOnly">
                    <span class="comment-text">{{ rowData.valuesByDate[dateKey]?.comment }}</span>
                  </div>
                  <!-- Entries with both value and comment -->
                  <div class="value-content" *ngIf="rowData.valuesByDate[dateKey]?.hasValue && rowData.valuesByDate[dateKey]?.hasComment && !rowData.valuesByDate[dateKey]?.isCommentOnly">
                    <span class="value">{{ rowData.valuesByDate[dateKey]?.value }}</span>
                    <span class="unit" *ngIf="rowData.unit">{{ rowData.unit }}</span>
                    <div class="comment-inline">
                      <span class="comment-text">{{ rowData.valuesByDate[dateKey]?.comment }}</span>
                    </div>
                  </div>
                  <!-- Entries with value only (no comment) -->
                  <div class="value-content" *ngIf="rowData.valuesByDate[dateKey]?.hasValue && !rowData.valuesByDate[dateKey]?.hasComment">
                    <span class="value">{{ rowData.valuesByDate[dateKey]?.value }}</span>
                    <span class="unit" *ngIf="rowData.unit">{{ rowData.unit }}</span>
                  </div>
                  <!-- Empty cells -->
                  <div class="no-value" *ngIf="!rowData.valuesByDate[dateKey]?.hasValue && !rowData.valuesByDate[dateKey]?.hasComment">
                    <span class="empty-cell">—</span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Comparison Mode -->
    <div class="comparison-mode" *ngIf="filteredMonitoringData.length > 0 && viewMode === 'compare'">
      <div class="comparison-header">
        <h3>
          <mat-icon>compare_arrows</mat-icon>
          Compare Lab Results
        </h3>
        <p>Select multiple tests to compare their trends over time</p>
      </div>

      <!-- Test Selection Checklist (only numeric/non-microbiology tests) -->
      <div class="test-selection">
        <div class="selection-header">
          <h4>Select Tests to Compare</h4>
          <span class="selection-count">{{ selectedForComparison.length }} selected</span>
          <p class="selection-note" *ngIf="numericTestsForComparison.length < filteredMonitoringData.length">
            <mat-icon>info</mat-icon>
            Only numeric tests are available for comparison. Microbiology and non-numeric tests are excluded.
          </p>
        </div>
        <div class="test-checklist">
          <div class="test-item" *ngFor="let item of numericTestsForComparison" 
               [class.selected]="isSelectedForComparison(item.testCode)"
               (click)="toggleComparisonSelection(item.testCode)">
            <input type="checkbox" 
                   [checked]="isSelectedForComparison(item.testCode)" 
                   (change)="toggleComparisonSelection(item.testCode)"
                   (click)="$event.stopPropagation()"
                   class="test-checkbox">
            <div class="test-info">
              <div class="test-name">{{ item.testName }}</div>
              <div class="test-details">
                <span class="test-code">{{ item.testCode }}</span>
                <span class="test-unit">{{ item.unit }}</span>
                <span class="test-status" [ngClass]="item.status">{{ item.status | titlecase }}</span>
              </div>
            </div>
            <div class="test-value">
              <span class="current-value">{{ item.latestValue }}</span>
              <span class="reference-range">{{ item.referenceRange }}</span>
            </div>
          </div>
          <div class="no-numeric-tests" *ngIf="numericTestsForComparison.length === 0">
            <mat-icon>info</mat-icon>
            <p>No numeric tests available for comparison. All available tests are microbiology or non-numeric.</p>
          </div>
        </div>
      </div>

      <!-- Comparison Chart -->
      <div class="comparison-chart" *ngIf="selectedForComparison.length > 0">
        <div class="chart-header">
          <h4>Trend Comparison</h4>
          <div class="chart-actions">
            <button mat-button (click)="selectedForComparison.length = 0; generateComparisonChartData()">
              <mat-icon>clear</mat-icon>
              Clear Selection
            </button>
          </div>
        </div>
        <div class="chart-container">
          <div class="chart-wrapper">
            <ngx-charts-line-chart 
              *ngIf="lineChartData.length > 0"
              class="ngx-chart" 
              [results]="lineChartData" 
              [xAxis]="true" 
              [yAxis]="true"
              [showXAxisLabel]="true" 
              [showYAxisLabel]="true"
              [xAxisLabel]="'Collection Date'" 
              [yAxisLabel]="yAxisLabel"
              [xAxisTickFormatting]="formatComparisonXAxisLabel"
              [scheme]="comparisonColorScheme" 
              [animations]="true" 
              [legend]="true" 
              [gradient]="false" 
              [autoScale]="true"
              [timeline]="false"
              [showGridLines]="true">
            </ngx-charts-line-chart>
            <div *ngIf="lineChartData.length === 0" class="no-data">
              <mat-icon>info</mat-icon>
              <p>No data available for selected tests</p>
            </div>
          </div>
        </div>
      </div>

      <!-- No Selection State -->
      <div class="no-selection" *ngIf="selectedForComparison.length === 0">
        <mat-icon>compare_arrows</mat-icon>
        <h4>No Tests Selected</h4>
        <p>Select numeric tests from the list above to compare their trends over time</p>
        <p class="note" *ngIf="numericTestsForComparison.length === 0">
          Note: Microbiology and non-numeric tests cannot be compared.
        </p>
      </div>
    </div>

    <!-- Detailed Trend View -->
    <div class="trend-detail-view" *ngIf="selectedTestData">
      <div class="trend-header">
        <div class="header-content">
          <h2>
            <mat-icon>trending_up</mat-icon>
            {{ selectedTestData.testName }} - Trend Analysis
          </h2>
          <div class="test-code-badge">{{ selectedTestData.testCode }}</div>
        </div>
        <button mat-icon-button (click)="closeTrendView()" class="close-trend-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      
      <div class="trend-content">
        <!-- Summary Cards Grid -->
        <div class="trend-summary" *ngIf="computedTrendData">
          <mat-card class="summary-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>science</mat-icon>
                <h3>Current Value</h3>
              </div>
              <div class="stat-value current-value">{{ computedTrendData.currentValue }}</div>
              <div class="stat-subtitle">Latest measurement</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="summary-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon [ngClass]="selectedTestData.status">{{ computedTrendData.statusIcon }}</mat-icon>
                <h3>Status</h3>
              </div>
              <div class="stat-value" [ngClass]="selectedTestData.status">
                {{ computedTrendData.statusText }}
              </div>
              <div class="stat-subtitle" *ngIf="computedTrendData.referenceRange && computedTrendData.referenceRange !== 'N/A'">
                Reference: {{ computedTrendData.referenceRange }}
              </div>
              <div class="stat-subtitle" *ngIf="selectedTestData.isMicrobiology">
                Microbiology test result
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="summary-card" *ngIf="!selectedTestData.isMicrobiology">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon [ngClass]="selectedTestData.trend">{{ computedTrendData.trendIcon }}</mat-icon>
                <h3>Trend</h3>
              </div>
              <div class="stat-value" [ngClass]="selectedTestData.trend">
                {{ computedTrendData.trendText }}
                <span *ngIf="computedTrendData.trendPercentage > 0" class="trend-percentage">
                  ({{ computedTrendData.trendPercentage | number:'1.1-1' }}%)
                </span>
              </div>
              <div class="stat-subtitle">Over {{ computedTrendData.timeRangeLabel }}</div>
            </mat-card-content>
          </mat-card>
          
          <mat-card class="summary-card" *ngIf="selectedTestData.isMicrobiology">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>bacteria</mat-icon>
                <h3>Type</h3>
              </div>
              <div class="stat-value">Microbiology</div>
              <div class="stat-subtitle">Non-numeric results</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="summary-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>schedule</mat-icon>
                <h3>Data Points</h3>
              </div>
              <div class="stat-value">{{ computedTrendData.dataPointsCount }}</div>
              <div class="stat-subtitle">Historical measurements</div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Chart Section (only for numeric/non-microbiology results) -->
        <div class="trend-chart" *ngIf="computedTrendData && !selectedTestData.isMicrobiology">
          <mat-card>
            <mat-card-header>
              <mat-card-title>
                <mat-icon>show_chart</mat-icon>
                Value History
              </mat-card-title>
              <mat-card-subtitle>{{ computedTrendData.timeRangeLabel }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div class="chart-container">
                <div class="chart-wrapper">
                  <ngx-charts-line-chart 
                    *ngIf="trendLineChartData.length > 0 && trendLineChartData[0].series.length > 0"
                    class="ngx-chart" 
                    [results]="trendLineChartData" 
                    [xAxis]="true" 
                    [yAxis]="true"
                    [showXAxisLabel]="true" 
                    [showYAxisLabel]="true"
                    [xAxisLabel]="'Collection Date'" 
                    [yAxisLabel]="'Value (' + computedTrendData.unit + ')'"
                    [scheme]="trendColorScheme" 
                    [animations]="true" 
                    [legend]="false" 
                    [gradient]="false" 
                    [autoScale]="true"
                    [timeline]="false"
                    [showGridLines]="true">
                  </ngx-charts-line-chart>
                  <div *ngIf="!trendLineChartData.length || (trendLineChartData.length > 0 && trendLineChartData[0].series.length === 0)" class="no-data">
                    <mat-icon>info</mat-icon>
                    <p>No data available for chart</p>
                  </div>
                </div>
                <div class="chart-info">
                  <span class="data-points">{{ computedTrendData.dataPointsCount }} data points</span>
                  <span class="unit">{{ computedTrendData.unit }}</span>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
        
        <!-- Comments Section for Microbiology -->
        <div class="trend-comments" *ngIf="computedTrendData && selectedTestData.isMicrobiology && selectedTestData.latestComment">
          <mat-card>
            <mat-card-header>
              <mat-card-title>
                <mat-icon>comment</mat-icon>
                Latest Comment
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="comment-content">
                {{ selectedTestData.latestComment }}
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Historical Data Table -->
        <div class="trend-table" *ngIf="computedTrendData">
          <mat-card>
            <mat-card-header>
              <mat-card-title>
                <mat-icon>history</mat-icon>
                Historical Data
              </mat-card-title>
              <mat-card-subtitle>Complete measurement history</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div class="data-table">
                <div class="table-header">
                  <div class="col-date">Date & Time</div>
                  <div class="col-value">Value</div>
                  <div class="col-status">Status</div>
                  <div class="col-change" *ngIf="!selectedTestData.isMicrobiology">Change</div>
                  <div class="col-comment" *ngIf="selectedTestData.isMicrobiology">Comment</div>
                </div>
                <div class="table-body">
                  <div *ngFor="let historyItem of computedTrendData.history; let i = index" 
                       class="table-row"
                       [class.highlight]="i === 0">
                    <div class="col-date">
                      <div class="date">{{ historyItem.formattedDate }}</div>
                      <div class="time">{{ historyItem.formattedTime }}</div>
                    </div>
                    <div class="col-value">
                      <span class="value">{{ historyItem.formattedValue }}</span>
                    </div>
                    <div class="col-status">
                      <span class="status-badge" [ngClass]="historyItem.status">
                        {{ historyItem.status | titlecase }}
                      </span>
                    </div>
                    <div class="col-change" *ngIf="!selectedTestData.isMicrobiology">
                      <span *ngIf="historyItem.hasChange" class="change-indicator" [ngClass]="historyItem.changeClass">
                        <mat-icon>{{ historyItem.changeIcon }}</mat-icon>
                        {{ historyItem.changeText }}
                      </span>
                      <span *ngIf="!historyItem.hasChange" class="no-change">—</span>
                    </div>
                    <div class="col-comment" *ngIf="selectedTestData.isMicrobiology">
                      <span *ngIf="historyItem.comment" class="comment-text">{{ historyItem.comment }}</span>
                      <span *ngIf="!historyItem.comment" class="no-comment">—</span>
                    </div>
                  </div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>
    </div>
  </div>
  </div>
</div>
